import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardService } from '../services/api';
import { FileText, Clock, CheckCircle, XCircle, AlertTriangle, DollarSign, BarChart3, Banknote, Users, TrendingUp, Activity, ArrowRight, CalendarDays, CreditCard } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

interface DashboardData {
  invoiceStats: { total: number; pending: number; in_process: number; completed: number; denied: number; paid: number; ajuste: number };
  chequeStats: { total: number; pending: number; in_process: number; completed: number; denied: number; paid: number };
  recentInvoices: any[];
  recentCheques: any[];
  pendingInvoices: any[];
  totalClients: number;
  todayInvoices: number;
  todayCheques: number;
  recentActivity: any[];
}

interface ClientDashboardData {
  invoiceStats: { total: number; pending: number; in_process: number; completed: number; denied: number; paid: number };
  chequeStats: { total: number; pending: number; in_process: number; completed: number; denied: number; paid: number };
  recentInvoices: any[];
  recentCheques: any[];
  pendingInvoices: any[];
}

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pendiente' },
  in_process: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En Proceso' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completada' },
  denied: { bg: 'bg-red-100', text: 'text-red-700', label: 'Denegada' },
  paid: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Pagada' },
  ajuste: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Ajuste' },
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [clientData, setClientData] = useState<ClientDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'invoices' | 'cheques'>('invoices');
  const [cardView, setCardView] = useState<'pending' | 'invoices' | 'cheques'>('pending');
  const navigate = useNavigate();

  const isClient = user?.role === 'client';
  const isClientAdmin = user?.clientRole === 'lab_owner' || user?.clientRole === 'client_admin';

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        if (user?.role === 'admin') {
          const res = await dashboardService.getStats();
          setData(res.data);
        } else if (user?.role === 'client') {
          const res = await dashboardService.getClientStats();
          setClientData(res.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboard();
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Client view
  if (isClient) {
    const cd = clientData;
    const cInv = cd?.invoiceStats || { total: 0, pending: 0, in_process: 0, completed: 0, paid: 0 };
    const cCk = cd?.chequeStats || { total: 0, pending: 0, in_process: 0, completed: 0, paid: 0 };

    return (
      <div className="space-y-5">
        {/* Header */}
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Bienvenido, {user?.firstName}</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>

        {/* Quick Stats Row - clickable cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div
            onClick={() => setCardView('invoices')}
            className={`rounded-xl border shadow-sm p-4 hover:shadow-md transition cursor-pointer ${
              cardView === 'invoices' ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : 'bg-white border-gray-100'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileText size={20} className="text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{cInv.total}</p>
            <p className="text-xs text-gray-500 mt-0.5">Total Facturas</p>
          </div>
          <div
            onClick={() => setCardView('cheques')}
            className={`rounded-xl border shadow-sm p-4 hover:shadow-md transition cursor-pointer ${
              cardView === 'cheques' ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200' : 'bg-white border-gray-100'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Banknote size={20} className="text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{cCk.total}</p>
            <p className="text-xs text-gray-500 mt-0.5">Total Cheques</p>
          </div>
          <div
            onClick={() => setCardView('pending')}
            className={`rounded-xl border shadow-sm p-4 hover:shadow-md transition cursor-pointer ${
              cardView === 'pending' ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200' : 'bg-white border-gray-100'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <AlertTriangle size={20} className="text-amber-600" />
              </div>
              <span className="text-[11px] font-medium bg-amber-50 text-amber-600 rounded-full px-2 py-0.5">atención</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{(cd?.pendingInvoices || []).length}</p>
            <p className="text-xs text-gray-500 mt-0.5">Pendientes</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                <DollarSign size={20} className="text-violet-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-800">{(cInv.completed || 0) + (cInv.paid || 0)}</p>
            <p className="text-xs text-gray-500 mt-0.5">Cobradas</p>
          </div>
        </div>

        {/* Main Content: Dynamic View */}
        <div className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
          cardView === 'pending' ? 'border-amber-200' : cardView === 'invoices' ? 'border-blue-200' : 'border-emerald-200'
        }`}>
          {/* Header */}
          <div className={`px-4 py-3 border-b flex items-center gap-2 ${
            cardView === 'pending' ? 'border-amber-100 bg-amber-50/50' : cardView === 'invoices' ? 'border-blue-100 bg-blue-50/50' : 'border-emerald-100 bg-emerald-50/50'
          }`}>
            {cardView === 'pending' && <AlertTriangle size={16} className="text-amber-600" />}
            {cardView === 'invoices' && <FileText size={16} className="text-blue-600" />}
            {cardView === 'cheques' && <Banknote size={16} className="text-emerald-600" />}
            <h3 className={`text-sm font-bold ${
              cardView === 'pending' ? 'text-amber-800' : cardView === 'invoices' ? 'text-blue-800' : 'text-emerald-800'
            }`}>
              {cardView === 'pending' ? 'Facturas Pendientes' : cardView === 'invoices' ? 'Facturas Recientes' : 'Cheques Recientes'}
            </h3>
            <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 ${
              cardView === 'pending' ? 'bg-amber-200 text-amber-800' : cardView === 'invoices' ? 'bg-blue-200 text-blue-800' : 'bg-emerald-200 text-emerald-800'
            }`}>
              {cardView === 'pending' ? (cd?.pendingInvoices || []).length : cardView === 'invoices' ? (cd?.recentInvoices || []).length : (cd?.recentCheques || []).length}
            </span>
            {cardView !== 'pending' && (
              <Link to={cardView === 'invoices' ? '/invoices' : '/my-cheques'} className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 ml-2">
                Ver todos <ArrowRight size={12} />
              </Link>
            )}
          </div>

          {/* Pending Invoices View */}
          {cardView === 'pending' && (
            (cd?.pendingInvoices || []).length > 0 ? (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                  <thead className="sticky top-0 z-10" style={{ backgroundColor: '#fffbeb' }}>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap"># Factura</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">Plan</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">Fecha</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">Estado</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">Pendiente</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(cd?.pendingInvoices || []).map((inv: any) => {
                      const st = statusColors[inv.status] || statusColors.pending;
                      return (
                        <tr key={inv.id} className="hover:bg-amber-50/30 cursor-pointer" onClick={() => navigate(`/invoices?invoice=${inv.id}`)}>
                          <td className="px-3 py-2.5 font-mono font-medium text-blue-600 whitespace-nowrap">{inv.invoice_number}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{inv.plan_code || '-'}</span></td>
                          <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-US') : '-'}</td>
                          <td className="px-3 py-2.5"><span className={`px-2 py-0.5 text-xs rounded-full ${st.bg} ${st.text}`}>{st.label}</span></td>
                          <td className="px-3 py-2.5 text-amber-700 text-xs max-w-[300px]"><div className="truncate" title={inv.pending_notes}>{inv.pending_notes || '-'}</div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-4 py-12 text-center text-gray-400">
                <CheckCircle size={40} className="mx-auto mb-2 text-emerald-300" />
                <p className="text-sm font-medium text-gray-500">No hay facturas pendientes</p>
              </div>
            )
          )}

          {/* Recent Invoices View */}
          {cardView === 'invoices' && (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500"># Factura</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plan</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Monto</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Pendiente</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(cd?.recentInvoices || []).map((inv: any) => {
                    const st = statusColors[inv.status] || statusColors.pending;
                    return (
                      <tr key={inv.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => navigate(`/invoices?invoice=${inv.id}`)}>
                        <td className="px-3 py-2.5 font-mono font-medium text-blue-600 whitespace-nowrap">{inv.invoice_number}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{inv.plan_code || '-'}</span></td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-US') : '-'}</td>
                        <td className="px-3 py-2.5 text-gray-700 font-medium whitespace-nowrap">${Number(inv.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2.5"><span className={`px-2 py-0.5 text-xs rounded-full ${st.bg} ${st.text}`}>{st.label}</span></td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs max-w-[250px]"><div className="truncate">{inv.pending_notes || '-'}</div></td>
                      </tr>
                    );
                  })}
                  {(cd?.recentInvoices || []).length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No hay facturas</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent Cheques View */}
          {cardView === 'cheques' && (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500"># Cheque</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plan</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha CK</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Notas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(cd?.recentCheques || []).map((ck: any) => {
                    const st = statusColors[ck.status] || statusColors.pending;
                    return (
                      <tr key={ck.id} className="hover:bg-emerald-50/50 cursor-pointer" onClick={() => navigate(`/my-cheques`)}>
                        <td className="px-3 py-2.5 font-mono font-medium text-emerald-600 whitespace-nowrap">{ck.check_number || '-'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{ck.plan || '-'}</span></td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{ck.fecha_ck ? new Date(ck.fecha_ck).toLocaleDateString('en-US') : '-'}</td>
                        <td className="px-3 py-2.5"><span className={`px-2 py-0.5 text-xs rounded-full ${st.bg} ${st.text}`}>{st.label}</span></td>
                        <td className="px-3 py-2.5 text-gray-600 text-xs max-w-[250px]"><div className="truncate">{ck.notas || '-'}</div></td>
                      </tr>
                    );
                  })}
                  {(cd?.recentCheques || []).length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No hay cheques</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    );
  }

  if (!data) return null;

  const invStats = data.invoiceStats;
  const ckStats = data.chequeStats;

  // Admin view
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Hola, {user?.firstName} {user?.lastName}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
            <CalendarDays size={14} className="text-blue-600" />
            <span className="text-xs font-semibold text-blue-700">Hoy: {data.todayInvoices} facturas, {data.todayCheques} cheques</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div
          onClick={() => setCardView('invoices')}
          className={`rounded-xl border shadow-sm p-4 hover:shadow-md transition cursor-pointer ${
            cardView === 'invoices' ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' : 'bg-white border-gray-100'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <span className="text-[11px] font-medium bg-blue-50 text-blue-600 rounded-full px-2 py-0.5">{data.todayInvoices} hoy</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{invStats.total}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Facturas</p>
        </div>
        <div
          onClick={() => setCardView('cheques')}
          className={`rounded-xl border shadow-sm p-4 hover:shadow-md transition cursor-pointer ${
            cardView === 'cheques' ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-200' : 'bg-white border-gray-100'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <Banknote size={20} className="text-emerald-600" />
            </div>
            <span className="text-[11px] font-medium bg-emerald-50 text-emerald-600 rounded-full px-2 py-0.5">{data.todayCheques} hoy</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{ckStats.total}</p>
          <p className="text-xs text-gray-500 mt-0.5">Total Cheques</p>
        </div>
        <div
          onClick={() => setCardView('pending')}
          className={`rounded-xl border shadow-sm p-4 hover:shadow-md transition cursor-pointer ${
            cardView === 'pending' ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200' : 'bg-white border-gray-100'
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <span className="text-[11px] font-medium bg-amber-50 text-amber-600 rounded-full px-2 py-0.5">atención</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{data.pendingInvoices?.length || 0}</p>
          <p className="text-xs text-gray-500 mt-0.5">Pendientes</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
              <Users size={20} className="text-violet-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-800">{data.totalClients}</p>
          <p className="text-xs text-gray-500 mt-0.5">Clientes</p>
        </div>
      </div>

      {/* Main Content: Dynamic View + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: Dynamic Content based on cardView */}
      <div className={`lg:col-span-2 bg-white rounded-xl border shadow-sm overflow-hidden ${
        cardView === 'pending' ? 'border-amber-200' : cardView === 'invoices' ? 'border-blue-200' : 'border-emerald-200'
      }`}>
        {/* Header */}
        <div className={`px-4 py-3 border-b flex items-center gap-2 ${
          cardView === 'pending' ? 'border-amber-100 bg-amber-50/50' : cardView === 'invoices' ? 'border-blue-100 bg-blue-50/50' : 'border-emerald-100 bg-emerald-50/50'
        }`}>
          {cardView === 'pending' && <AlertTriangle size={16} className="text-amber-600" />}
          {cardView === 'invoices' && <FileText size={16} className="text-blue-600" />}
          {cardView === 'cheques' && <Banknote size={16} className="text-emerald-600" />}
          <h3 className={`text-sm font-bold ${
            cardView === 'pending' ? 'text-amber-800' : cardView === 'invoices' ? 'text-blue-800' : 'text-emerald-800'
          }`}>
            {cardView === 'pending' ? 'Facturas Pendientes que Necesitan Atención' : cardView === 'invoices' ? 'Facturas Recientes' : 'Cheques Recientes'}
          </h3>
          <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 ${
            cardView === 'pending' ? 'bg-amber-200 text-amber-800' : cardView === 'invoices' ? 'bg-blue-200 text-blue-800' : 'bg-emerald-200 text-emerald-800'
          }`}>
            {cardView === 'pending' ? data.pendingInvoices.length : cardView === 'invoices' ? data.recentInvoices.length : data.recentCheques.length}
          </span>
        </div>

        {/* Pending Invoices View */}
        {cardView === 'pending' && (
          data.pendingInvoices.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" style={{ minWidth: '900px' }}>
                <thead className="sticky top-0 z-10" style={{ backgroundColor: '#fffbeb' }}>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap w-[100px]"># Factura</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap w-[180px]">Cliente</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap w-[80px]">Plan</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap w-[100px]">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap w-[80px]">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap">Pendiente</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap w-[90px]">Facturador</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.pendingInvoices.map((inv: any) => {
                    const st = statusColors[inv.status] || statusColors.pending;
                    return (
                      <tr key={inv.id} className="hover:bg-amber-50/30 cursor-pointer" onClick={() => navigate(`/clients?selected=${inv.client_id}&invoice=${inv.id}`)}>
                        <td className="px-3 py-2.5 font-mono font-medium text-blue-600 whitespace-nowrap">{inv.invoice_number}</td>
                        <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{inv.company_name || inv.client_name || '-'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{inv.plan_code || '-'}</span></td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-US') : '-'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><span className={`px-2 py-0.5 text-xs rounded-full ${st.bg} ${st.text}`}>{st.label}</span></td>
                        <td className="px-3 py-2.5 text-amber-700 text-xs max-w-[300px]" title={inv.pending_notes}><div className="truncate">{inv.pending_notes || '-'}</div></td>
                        <td className="px-3 py-2.5 whitespace-nowrap"><span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">{inv.initials || '-'}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-12 text-center text-gray-400">
              <CheckCircle size={40} className="mx-auto mb-2 text-emerald-300" />
              <p className="text-sm font-medium text-gray-500">No hay facturas pendientes de atención</p>
            </div>
          )
        )}

        {/* Recent Invoices View */}
        {cardView === 'invoices' && (
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500"># Factura</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cliente</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plan</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Facturador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recentInvoices.map((inv: any) => {
                  const st = statusColors[inv.status] || statusColors.pending;
                  return (
                    <tr key={inv.id} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => navigate(`/clients?selected=${inv.client_id}&invoice=${inv.id}`)}>
                      <td className="px-3 py-2.5 font-mono font-medium text-blue-600">{inv.invoice_number}</td>
                      <td className="px-3 py-2.5 text-gray-700 truncate max-w-[160px]">{inv.company_name || inv.client_name || '-'}</td>
                      <td className="px-3 py-2.5"><span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{inv.plan_code || '-'}</span></td>
                      <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-US') : '-'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><span className={`px-2 py-0.5 text-xs rounded-full ${st.bg} ${st.text}`}>{st.label}</span></td>
                      <td className="px-3 py-2.5"><span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">{inv.initials || '-'}</span></td>
                    </tr>
                  );
                })}
                {data.recentInvoices.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No hay facturas recientes</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Recent Cheques View */}
        {cardView === 'cheques' && (
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500"># Cheque</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Cliente</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Plan</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Fecha CK</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Estado</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Facturador</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recentCheques.map((ck: any) => {
                  const st = statusColors[ck.status] || statusColors.pending;
                  return (
                    <tr key={ck.id} className="hover:bg-emerald-50/50 cursor-pointer" onClick={() => navigate(`/clients?selected=${ck.client_id}`)}>
                      <td className="px-3 py-2.5 font-mono font-medium text-emerald-600">{ck.check_number || '-'}</td>
                      <td className="px-3 py-2.5 text-gray-700 truncate max-w-[160px]">{ck.company_name || ck.client_name || '-'}</td>
                      <td className="px-3 py-2.5"><span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs">{ck.plan || '-'}</span></td>
                      <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">{ck.fecha_ck ? new Date(ck.fecha_ck).toLocaleDateString('en-US') : '-'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap"><span className={`px-2 py-0.5 text-xs rounded-full ${st.bg} ${st.text}`}>{st.label}</span></td>
                      <td className="px-3 py-2.5"><span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">{ck.facturador || '-'}</span></td>
                    </tr>
                  );
                })}
                {data.recentCheques.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400">No hay cheques recientes</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Right: Activity Feed */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Activity size={16} className="text-purple-600" />
          <h3 className="text-sm font-bold text-gray-700">Actividad Reciente</h3>
        </div>
        <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-50">
          {data.recentActivity.map((act: any, idx: number) => (
            <div key={idx} className="px-4 py-3 hover:bg-gray-50/50 transition cursor-pointer" onClick={() => act.client_id && navigate(`/clients?selected=${act.client_id}`)}>
              <div className="flex items-start gap-2.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${act.action_type === 'invoice_created' ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                  {act.action_type === 'invoice_created' ? (
                    <FileText size={13} className="text-blue-600" />
                  ) : (
                    <Banknote size={13} className="text-emerald-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-800">
                    <span className="font-semibold">{act.actor || '—'}</span>
                    {' '}
                    {act.action_type === 'invoice_created' ? 'creó factura' : 'creó cheque'}
                    {' '}
                    <span className="font-mono font-semibold text-blue-600">#{act.item_label}</span>
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                    {act.client_name || '-'} · {act.plan_code || '-'} · {new Date(act.action_date).toLocaleDateString('en-US')}
                  </p>
                </div>
              </div>
            </div>
          ))}
          {data.recentActivity.length === 0 && (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">Sin actividad reciente</div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Dashboard;
